import React from 'react';

import {
  Stack,
  Center,
  StackDirection,
  RadioProps as ChakraRadioProps,
  useRadioGroup,
  Box,
  Text,
  useRadio,
  forwardRef,
  Flex,
} from '@chakra-ui/react';

interface RadioProps extends ChakraRadioProps {
  direction?: StackDirection
  options: RadioType[]
  onChange?: (_v: any) => void
  size?: string
  defaultValue?: string
  label?: string
}

type RadioType = {
  label: string
  value: string
}

function RadioCard({ children, ...props }) {
  const { getInputProps, getCheckboxProps } = useRadio(props);
  const {
    isChecked, isDisabled, size, fontSize,
  } = props;
  const input = getInputProps();
  const checkbox = getCheckboxProps();
  return (
    <Box as="label">
      <Stack
        align="center"
        direction="row">
        <input {...input} />
        <Center
          align="center"
          justifyContent="center"
          {...checkbox}
          cursor="pointer"
          borderWidth="1px"
          borderRadius="full"
          borderColor="gray.600"
          _checked={{
            color: 'secondary.default',
            borderWidth: '2px',
            borderColor: isDisabled ? 'gray.400' : 'secondary.default',
          }}
          w={size}
          h={size}>
          {!!isChecked && (
            <Box
              borderRadius="full"
              bg={isDisabled ? 'gray.400' : 'secondary.default'}
              w="6px"
              h="6px"
            />
          )}
        </Center>
        <Text
          color={isDisabled ? 'gray.400' : 'gray.600'}
          variant={fontSize}>{children}
        </Text>
      </Stack>
    </Box>
  );
}

const Radio = forwardRef(({
  options = [],
  isDisabled,
  defaultValue,
  name = 'radioGroup',
  size = '4',
  fontSize = 'smallSemiBold',
  onChange = () => null,
  value,
  label,
  ...props
}: RadioProps, ref:any) => {
  const { getRootProps, getRadioProps } = useRadioGroup({
    name,
    defaultValue,
    onChange,
    value,
  });

  const group = getRootProps();
  return (
    <Stack
      {...props}>
      <Flex
        ml={2}
        hidden={!label}>
        <Text>{label}</Text>
      </Flex>
      <Stack
        direction="row"
        {...group}>
        {options.map(({ label: option, value: val }) => {
          const radio = getRadioProps({ value: val });
          return (
            <RadioCard
              key={`radio-${val}`}
              size={size}
              fontSize={fontSize}
              isDisabled={isDisabled}
              ref={ref}
              value={value}
              {...props}
              {...radio}>
              {option}
            </RadioCard>
          );
        })}
      </Stack>
    </Stack>
  );
});

export default Radio;
